import React, { useEffect, useRef } from 'react';
import PhoneInput from 'react-phone-input-2';

import InputBase from 'apps/auth/components/form-multi/InputBase';
import { clearErrorField, notValidateUpdateValue, validationAndUpdateValue } from 'apps/auth/components/form-multi/utils';
import { PHONE, PHONE_STRICT, TEL } from 'apps/auth/constants/FieldType';
import { EXCLUDE_COUNTRIES } from 'apps/auth/constants/utilsMasks';
import { logError } from 'apps/auth/helpers/clientLogger';
import { inputPropType } from 'apps/auth/helpers/customPropTypes';
import { getPhoneNumberSuggestion } from 'apps/auth/helpers/phoneNumberValidation';
import { toUpperCase } from 'apps/auth/helpers/utils';
import { useCountryMobileMask } from 'apps/auth/hooks/useCountryMobileMask';
import { useLibPhonenumberFormatter } from 'apps/auth/hooks/useLibPhonenumberFormatter';

const useSearchCountry = (ref, shouldInit, defaultCountry, field) => {
    useEffect(() => {
        if (!shouldInit) return;
        if (!ref.current) return;

        const hasValue = !!(field?.value && String(field.value).length > 0);
        if (hasValue) return;
        ref.current.updateCountry(defaultCountry);
    }, [shouldInit, defaultCountry, field?.value]);
};

const useChangeDefaultCountry = (ref, shouldInit, notFirstRender, defaultCountry, field) => {
    useEffect(() => {
        if (!shouldInit) return;
        if (!ref.current) return;

        if (!notFirstRender.current) {
            notFirstRender.current = true;
            return;
        }

        const hasValue = !!(field?.value && String(field.value).length > 0);
        if (hasValue) return;
        ref.current.updateCountry(defaultCountry);
    }, [shouldInit, defaultCountry, field?.value]);
};

const updateCountrySafe = (ref, iso2) => {
    if (ref.current) ref.current.updateCountry(iso2);
};

const writeValue = (props, fieldName, next) => props.changeFormFieldValue?.(props.formName, next, fieldName);

const clearThenRestore = (props, fieldName, value) => writeValue(props, fieldName, '').then(() => writeValue(props, fieldName, value));

const makeSwapId = (fromIso2, toIso2, value) => `${fromIso2}_to_${toIso2}:${value}`;

const runOncePerKey = (refKey, key) => {
    if (refKey.current === key) return false;
    refKey.current = key;
    return true;
};

const NewInputPhoneHOC = ({
    defaultCountry = 'gb',
    submitByEnter = false,
    focus = false,
    isPhoneTypeStrict = true,
    enablePhoneSuggestion = false,
    ...props
}) => {
    const ref = useRef(null);
    const notFirstRender = useRef(false);

    const simplifyPrefix = (prefix) => {
        if (prefix) {
            return prefix.toString().replace('+', '');
        }
        return '';
    };
    const lastPrefixRef = useRef(simplifyPrefix(props.prefix));

    // to handle +1 code country swap (North American Numbering Plan-NANP): US, CA, PR
    const prevNanpIso2Ref = useRef(defaultCountry);
    const nanpSwapIdRef = useRef('');

    // to remember last original iso2 we were on (not the aliased one)
    const prevOriginalIso2Ref = useRef(defaultCountry);
    const ccCxToAuFixIdRef = useRef('');

    const prevPrefixRef = useRef(null);

    const { defaultMask, masks, isReady, isMaskReadyForIso2, effectiveIso2 } = useCountryMobileMask(defaultCountry);
    const shouldInit = isReady && isMaskReadyForIso2 && !!props.iso2;
    const { ready: isFormatterReady, formatIntlPhoneNumber } = useLibPhonenumberFormatter();

    const { field } = props;

    useSearchCountry(ref, shouldInit, defaultCountry, field);
    useChangeDefaultCountry(ref, shouldInit, notFirstRender, defaultCountry, field);

    useEffect(() => {
        if (focus) {
            props.setIsFocused?.(true);
            props.setFocusOnLogin?.(false);
        }
    }, [focus]);

    // Instant dial code rewrite when country/prefix changes
    useEffect(() => {
        if (!shouldInit || !ref.current) return;
        const newPrefix = simplifyPrefix(props.prefix);
        if (!newPrefix) return;
        const prevPrefix = prevPrefixRef.current;
        const current = props.field?.value || '';
        if (prevPrefix === null) {
            if (current && !current.startsWith(newPrefix)) {
                props.changeFormFieldValue?.(props.formName, newPrefix + current, field?.name);
            }
            prevPrefixRef.current = newPrefix;
            lastPrefixRef.current = newPrefix;
            return;
        }
        if (prevPrefix === newPrefix) return;
        ref.current.updateCountry(defaultCountry);
        if (current.startsWith(newPrefix)) {
            prevPrefixRef.current = newPrefix;
            lastPrefixRef.current = newPrefix;
            return;
        }
        let next = current;
        if (current.startsWith(prevPrefix)) {
            const nsn = current.slice(prevPrefix.length);
            if (nsn && nsn.length > 0) {
                next = newPrefix + nsn;
            }
        }
        if (next !== current) {
            props.changeFormFieldValue?.(props.formName, next, field?.name);
        }
        prevPrefixRef.current = newPrefix;
        lastPrefixRef.current = newPrefix;
    }, [props.prefix, defaultCountry, shouldInit]);

    // Handle +1 code country swap (North American Numbering Plan-NANP): US, CA, PR
    useEffect(() => {
        if (defaultCountry === 'cc' || defaultCountry === 'cx') return;
        if (!shouldInit || !ref.current) return;
        const prev = prevNanpIso2Ref.current;
        const curr = defaultCountry;
        const NANP_COUNTRIES = new Set(['us', 'ca', 'pr']);
        const isNanpSwap = prev !== curr && NANP_COUNTRIES.has(prev) && NANP_COUNTRIES.has(curr);
        if (isNanpSwap) {
            const prevValue = props.field?.value || '';
            if (prevValue) {
                updateCountrySafe(ref, curr);
                const swapId = makeSwapId(prev, curr, prevValue);
                if (runOncePerKey(nanpSwapIdRef, swapId)) {
                    clearThenRestore(props, field?.name, prevValue);
                }
            }
        }
        prevNanpIso2Ref.current = curr;
    }, [defaultCountry, shouldInit, props.field?.value, props.formName, field?.name]);

    // Handle cc/cx → au swap: restore controlled value after internal reset
    useEffect(() => {
        if (!shouldInit || !ref.current) return;
        const prevIso2 = prevOriginalIso2Ref.current;
        const currIso2 = defaultCountry;
        const isCcCxToAu = (prevIso2 === 'cc' || prevIso2 === 'cx') && currIso2 === 'au';
        if (isCcCxToAu) {
            const prevValue = props.field?.value || '';
            if (prevValue) {
                updateCountrySafe(ref, 'au');
                const fixId = makeSwapId(prevIso2, currIso2, prevValue);
                if (runOncePerKey(ccCxToAuFixIdRef, fixId)) {
                    clearThenRestore(props, field?.name, prevValue);
                }
            }
        }
        prevOriginalIso2Ref.current = currIso2;
    }, [defaultCountry, shouldInit, props.field?.value, props.formName, field?.name]);

    const handlerKeyDown = (e) => {
        if (e.keyCode === 13 && submitByEnter && props.submitRef && props.submitRef.current) {
            const { selectedCountry } = ref.current?.state || {};
            const value = phoneNumberValidation(e);
            if (props.shouldValidateUserNumberFormat?.()) {
                props.validateNumberFormat?.(value, toUpperCase(props.iso2));
            } else {
                if (value !== selectedCountry?.countryCode && props.submitNumberOnLogin) {
                    validationAndUpdateValue(props.formName, value, field, props.changeFormField)
                        .catch((validateEvent) => logError(validateEvent))
                        .finally(() => props.submitNumberOnLogin());
                } else {
                    props.submitRef.current?.click();
                }
            }
        }
    };

    const handlerBlur = (e) => {
        const { selectedCountry } = ref.current?.state || {};
        if (!field.isError) {
            props.setIsFocused?.(false);
        }
        const value = phoneNumberValidation(e);
        if (value !== selectedCountry?.countryCode && !props.shouldValidateUserNumberFormat?.()) {
            validationAndUpdateValue(props.formName, value, field, props.changeFormField)
                .then(() => props.onBlurHandler && props.onBlurHandler(field.value, field))
                .catch((blureValidateEvent) => logError(blureValidateEvent));
        }
        if (enablePhoneSuggestion && isFormatterReady && value && value !== selectedCountry?.countryCode) {
            const iso2ForSuggestion = selectedCountry?.iso2 || props.iso2;
            const { suggestion } = getPhoneNumberSuggestion(value, iso2ForSuggestion);
            if (suggestion) {
                props.setPhoneSuggestion?.({ enteredNumber: value, suggestedNumber: suggestion, countryCode: iso2ForSuggestion });
                props.changeFormField?.(props.formName, { name: field.name, isError: true, errorMessage: null });
            } else if (props.phoneSuggestion) {
                props.setPhoneSuggestion?.(null);
                props.changeFormField?.(props.formName, { name: field.name, isError: false });
            }
        }
    };

    const phoneNumberValidation = (e) => {
        const rawFormattedValue = ref?.current?.numberInputRef?.value || '';
        const { selectedCountry } = ref.current?.state || {};
        const value = (e.target.value || '').replace(/\D+/g, '');
        const iso2ForFormat = selectedCountry?.iso2 || props.iso2;
        const canonicalFormatted = isFormatterReady && value && iso2ForFormat ? formatIntlPhoneNumber(value, iso2ForFormat) : rawFormattedValue;
        props.setFormattedNumber?.(canonicalFormatted);
        if (props.userInputRef) {
            props.userInputRef.current = value;
        }
        if (props.getUserInputWithoutPrefix && selectedCountry?.countryCode) {
            props.getUserInputWithoutPrefix(value.replace(selectedCountry.countryCode, ''));
        }
        return value;
    };

    const handlerFocus = () => {
        props.setIsFocused?.(true);
        const isError = field?.isError ?? false;
        if (isError) {
            clearErrorField(props.formName, field, props.changeFormField)
                .then(() => props.onFocus && props.onFocus())
                .catch((e) => logError(e));
            props.setLoginInputError?.(false);
        }
    };

    const handlerChange = (value, data) => {
        if (!props.isFocused) {
            props.setIsFocused?.(true);
        }
        notValidateUpdateValue(props.formName, value, field, props.changeFormField)
            .then((phone) => props.onChangeHandler && props.onChangeHandler(phone.value, data))
            .catch((e) => logError(e));
    };

    return (
        <>
            {shouldInit && (
                <PhoneInput
                    key={`${effectiveIso2}`}
                    ref={ref}
                    inputProps={{
                        name: field.name,
                        autoFocus: focus,
                        autoComplete: 'tel',
                    }}
                    specialLabel={''}
                    value={field.value || ''}
                    onlyCountries={[effectiveIso2]}
                    disableDropdown
                    countryCodeEditable={!isPhoneTypeStrict}
                    placeholder={''}
                    formName={field.canonicalPath || field.name}
                    isFocused={props.isFocused}
                    country={effectiveIso2}
                    defaultMask={defaultMask}
                    masks={masks}
                    excludeCountries={EXCLUDE_COUNTRIES}
                    isFilled={props.isFilled}
                    onChange={handlerChange}
                    onKeyDown={handlerKeyDown}
                    onFocus={handlerFocus}
                    onBlur={handlerBlur}
                    enableLongNumbers={16}
                    enableTerritories={true}
                />
            )}
        </>
    );
};

const NewInputPhone = InputBase(NewInputPhoneHOC);

NewInputPhone.propTypes = inputPropType([PHONE, PHONE_STRICT, TEL]);

export default NewInputPhone;
